import { t, Trans } from "@lingui/macro";
import SearchIcon from "@mui/icons-material/Search";
import { Box, CircularProgress, Container, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { useAuthState } from "@saleor/sdk";
import isEqual from "lodash/isEqual";
import React, { useState } from "react";

import {
	AttributeFragment,
	CartProductVariantFragment,
	CheckoutDetailsFragment,
	GmClientFragment,
	GmProfileFragment,
	GoalsMapperClientFilterInput,
	ProductVariantFragment,
	ProfileDateOfBirthFilterEnum,
	ProfileGenderTypeEnum,
	ProfileMaritalStatusEnum,
	useCheckoutBulkCreateMutation,
	useGoalsmapperClientsQuery,
	User,
	UserDraftCheckoutsDocument,
	useUserDraftCheckoutsQuery,
} from "@/saleor/api";

import { normalizePreferenceValue } from "../personalize/utils";
import useChannelContext from "../shared/contexts/ChannelContext";
import GenericDialog from "../shared/dialog/GenericDialog";
import { cloneAddress } from "../utils/address";
import ClientFilterButton from "./ClientFilterButton";
import { FilerOptions } from "./ClientFilterDialog";
import CreateContactButton from "./CreateContactButton";
import GMClientTable from "./GMClientTable";

export interface SelectClientDialogProps {
	onClose: () => void;
	isOpen: boolean;
	onShopForClients?: () => void;
	variantQuantityAvailable?: number;
	onCompleted?: () => void;
	variant?: CartProductVariantFragment;
	clients?: GmClientFragment[];
	selectedVariant?: ProductVariantFragment | null | undefined;
}

const SelectClientDialog = ({
	isOpen,
	onClose,
	clients: initialClients,
	onShopForClients,
	selectedVariant,
}: SelectClientDialogProps) => {
	const { user } = useAuthState();
	const { channel } = useChannelContext();
	const [filter, setFilter] = useState<GoalsMapperClientFilterInput>(initialClients ? {} : {});
	const [selectedClients, setSelectedClients] = useState<GmClientFragment[]>(
		initialClients || []
	);

	const [bulkCreateCheckout, { loading: bulkCreateProcessing }] = useCheckoutBulkCreateMutation({
		refetchQueries: [
			{
				query: UserDraftCheckoutsDocument,
				variables: { first: 100, channel, multipleCheckout: false },
			},
		],
		awaitRefetchQueries: true,
	});

	const { data: checkoutData } = useUserDraftCheckoutsQuery({
		variables: { first: 100, channel, multipleCheckout: false },
	});

	const { loading, data, refetch } = useGoalsmapperClientsQuery({
		variables: {
			filter,
			first: 100,
			after: null,
		},
	});

	const checkouts = checkoutData?.userDraftCheckouts?.edges.map((e) => e.node);
	if (!checkouts || !checkouts[0] || !checkouts[0].lines) {
		return null;
	}

	const variant = checkouts[0].lines[0]?.variant;
	let enoughStocks = true;
	let availableStock = 0;
	if (variant) {
		enoughStocks = (variant?.quantityAvailable || 0) >= selectedClients.length;
		availableStock = variant.quantityAvailable > 0 ? variant.quantityAvailable : 0;
	} else if (selectedVariant) {
		enoughStocks = selectedVariant.quantityAvailable >= selectedClients.length;
		availableStock =
			selectedVariant.quantityAvailable > 0 ? selectedVariant.quantityAvailable : 0;
	}

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFilter({
			...filter,
			search: event.target.value,
		});
	};

	const handleStartSearch = () => {
		setFilter({});
	};

	const onFilterChanged = (filterOps: FilerOptions | null) => {
		if (!filterOps) {
			setFilter({ search: filter?.search });
		} else {
			const newFilter: GoalsMapperClientFilterInput = {
				search: filter?.search,
				age: {
					gte: filterOps.startAge,
					lte: filterOps.endAge,
				},
			};
			if (filterOps.maritalStatus) {
				newFilter.maritalStatus = filterOps.maritalStatus as ProfileMaritalStatusEnum;
			}
			if (filterOps.birthday) {
				newFilter.dateOfBirth = filterOps.birthday as ProfileDateOfBirthFilterEnum;
			}
			if (filterOps.gender) {
				newFilter.gender = filterOps.gender as [ProfileGenderTypeEnum];
			}
			setFilter(newFilter);
		}
	};

	const clonePreferences = (checkout?: CheckoutDetailsFragment, profile?: GmProfileFragment) => {
		if (!checkout) {
			return null;
		}
		if (!checkout?.lines || checkout.lines.length === 0) {
			return null;
		}
		return checkout?.lines[0]?.preferences
			?.filter((p) => !!p && p.attribute)
			.map((pref) => ({
				attributeId: pref!.attribute!.id,
				preferenceValue:
					normalizePreferenceValue(
						pref!.attribute! as AttributeFragment,
						pref?.value,
						profile || checkout?.profile,
						user as User
					) || "",
			}));
	};

	const prepareLinesVariable = () => {
		if (!variant) {
			return selectedClients.map((client) => {
				return {
					profileId: client.person.id,
					checkoutLines: [],
					shipToAdvisor: false,
				};
			});
		}

		const checkoutMap =
			checkouts.reduce<{ [key: string]: CheckoutDetailsFragment }>((a, c) => {
				if (c.profile) {
					a[c.profile.id] = c;
				}
				return a;
			}, {}) || {};

		return selectedClients.map((client) => {
			const currentCheckout = checkoutMap[client.person.id];
			return {
				profileId: client.person.id,
				checkoutLines: [
					{
						quantity: 1,
						variantId: variant.id,
						preferences: currentCheckout
							? clonePreferences(currentCheckout)
							: clonePreferences(checkouts[0], client.person),
					},
				],
				shipToAdvisor: currentCheckout?.shipToAdvisor || false,
			};
		});
	};

	const handleCreateList = () => {
		if (bulkCreateProcessing) {
			return;
		}

		const firstCheckout = checkoutData?.userDraftCheckouts?.edges[0].node;
		const selectedDeliveryDate = firstCheckout?.selectedDeliveryDate;
		const initialProfileIds = initialClients?.map((c) => c.person.id) || [];
		const selectedProfileIds = selectedClients.map((c) => c.person.id);
		if (!firstCheckout || isEqual(initialProfileIds?.sort(), selectedProfileIds.sort())) {
			onShopForClients && onShopForClients();
			return;
		}

		const lines = prepareLinesVariable();
		const billingAddress = firstCheckout.billingAddress;
		return bulkCreateCheckout({
			variables: {
				channel: channel,
				profileIds: selectedProfileIds,
				lines,
				billingAddress: cloneAddress(billingAddress),
				selectedDeliveryDate: selectedDeliveryDate,
			},
		}).then((response) => {
			onShopForClients && onShopForClients();
		});
	};

	const onCancel = () => {
		setSelectedClients([]);
		onClose && onClose();
	};

	const renderActions = () => {
		if (!filter) {
			return null;
		}
		return (
			<Stack direction="row" justifyContent="flex-end">
				<Button className="ml-6" variant="outlined" onClick={onCancel}>
					<Trans>Cancel</Trans>
				</Button>
				<Button
					disabled={!enoughStocks || bulkCreateProcessing || selectedClients.length === 0}
					variant="contained"
					color="primary"
					className="ml-6"
					onClick={handleCreateList}
					endIcon={bulkCreateProcessing && <CircularProgress size={20} />}
				>
					<Trans>Done</Trans>
				</Button>
			</Stack>
		);
	};

	const onCreateContactCompleted = () => {
		refetch();
	};

	const renderClients = () => {
		if (!filter) {
			return null;
		}
		return (
			<GMClientTable
				onSelectedChange={(clientIds, clients) => setSelectedClients(clients)}
				selectedIds={selectedClients.map((c) => c.person.id)}
				idIdentity="person.id"
				emptyMessage={t`You don't have any contacts! Please use Create Contact button above to add new.`}
				selectedClients={selectedClients}
				filter={filter}
			/>
		);
	};

	return (
		<GenericDialog
			isOpen={isOpen}
			onClose={onClose}
			maxWidth="md"
			title={t`Select Recipients to Shop for`}
		>
			<Container maxWidth="md" disableGutters>
				<Box display="flex" flexDirection="column" className="w-full">
					<Stack direction="row" spacing={1}>
						<Input
							id="input-with-icon-adornment"
							className="flex-1"
							startAdornment={
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							}
							endAdornment={
								<InputAdornment position="end">
									<ClientFilterButton onFilterChanged={onFilterChanged} />
								</InputAdornment>
							}
							placeholder={t`Search by Contact’s Name, Contact Number or Email`}
							value={filter?.search}
							onChange={handleChange}
						/>
						<CreateContactButton onCompleted={onCreateContactCompleted} />
					</Stack>
					{!filter && (
						<Button
							variant="contained"
							color="primary"
							fullWidth={false}
							className="mt-4"
							onClick={handleStartSearch}
						>
							<Trans>Search for Clients</Trans>
						</Button>
					)}
					{renderClients()}
					{!enoughStocks && (
						<section className="col-span-3 text-center">
							<Stack spacing={2} alignItems="center" justifyContent="center">
								<div>
									<div className="text-s font-semibold text-red-700">
										<Trans>
											There are only {availableStock} items of the product in
											Stock.
										</Trans>
									</div>
								</div>
							</Stack>
						</section>
					)}
					{renderActions()}
				</Box>
			</Container>
		</GenericDialog>
	);
};

export default SelectClientDialog;
